import useUiNotification from '~/composables/useUiNotification';
import { useBFF } from '~/composables/useBFF/useBFF';
import { cartGetters } from '~/composables/getters';
import type { Cart, Maybe } from '~/types/GraphQL';
import { getErrorMessage } from '~/helpers/utils/getErrorMessage';

const ERROR_MESSAGES = {
  MULTIPLE_COUPONS: 'You can not use more than 1 coupon code',
  INVALID_CODE: 'This code is invalid.',
  PROBLEM_REMOVING_COUPON: 'Something wrong happened when removing coupon',
};
interface UseExtendedCartErrors {
  applyOrRemoveCoupon: string | undefined;
  updateItemsInCart: string | undefined;
  deleteActiveCarts?: string;
  createCart?: string;
  addItem?: string;
  setNewletter?: string;
}

const loading = ref(false);
const updating = ref(false);
const cart = ref<Maybe<Cart> | undefined>();

export const useCart = () => {
  const { $carts, $me } = useBFF();

  const load = async () => {
    const { data: profileData } = await $me.getMe();
    setCart(profileData?.activeCart);
    return profileData?.activeCart;
  };

  const { send } = useUiNotification();

  const setCart = async (data: Maybe<Cart> | undefined | null, validateIfEmpty = true) => {
    if (!data && validateIfEmpty) {
      await refresh();
      return;
    }

    cart.value = data;
  };

  const error = shallowRef<UseExtendedCartErrors>(
    {
      applyOrRemoveCoupon: undefined,
      updateItemsInCart: undefined,
      deleteActiveCarts: undefined,
      createCart: undefined,
      addItem: undefined,
      setNewletter: undefined,
    },
  );

  const discountCodes = computed(() => cartGetters.getCoupons(cart.value as Cart));
  const loyaltyCouponCode = computed(() => cartGetters.getLoyaltyCouponCode(cart.value as Cart));

  const refresh = async () => {
    loading.value = true;

    try {
      if (cart.value) {
        const { data: cartVersion } = await $carts.checkCartVersion(cart.value.id);

        if (cartVersion === cart.value.version) {
          return;
        }
      }

      const { data: profileData } = await $me.getMe();

      if (profileData?.activeCart) {
        await setCart(profileData.activeCart, false);
      }
    }
    catch {
      // do nothing
    }
    finally {
      loading.value = false;
    }
  };

  const applyCoupon = async (couponCode: string) => {
    if (couponCode === discountCodes.value?.[0]?.code || couponCode === loyaltyCouponCode?.value?.value) {
      return;
    }

    try {
      if (discountCodes.value?.length > 0 || loyaltyCouponCode?.value) {
        send({
          message: ERROR_MESSAGES.MULTIPLE_COUPONS,
          type: 'warning',
        });
        return;
      }

      updating.value = true;
      error.value.applyOrRemoveCoupon = undefined;
      const { data } = await $carts.applyCoupon(cart.value?.id, cart.value?.version, { code: couponCode });

      await setCart(data?.cart);
    }
    catch (err: any) {
      switch (err.error) {
        case 'Redemption requirements are not met':
          error.value.applyOrRemoveCoupon = err.error;
          send({
            message: err.error,
            type: 'warning',
          });
          break;
        case 'Please log into your online shop account to take advantage of the price advantage':
          error.value.applyOrRemoveCoupon = err.error;
          send({
            message: err.error,
            type: 'warning',
          });
          break;
        case 'Loyalty discount code not found':
          error.value.applyOrRemoveCoupon = err.error;
          send({
            message: err.error,
            type: 'danger',
          });
          break;
        case 'Loyalty discount code already applied':
          error.value.applyOrRemoveCoupon = err.error;
          send({
            message: err.error,
            type: 'danger',
          });
          break;
        default:
          error.value.applyOrRemoveCoupon = ERROR_MESSAGES.INVALID_CODE;
          send({
            message: ERROR_MESSAGES.INVALID_CODE,
            type: 'danger',
          });
          break;
      }
      return;
    }
    finally {
      updating.value = false;
    }
  };

  const removeCoupon = async (couponCode: string) => {
    const discountId = discountCodes.value.find(discountCode => discountCode?.code === couponCode)?.id;
    if (!discountId && loyaltyCouponCode.value?.value !== couponCode) {
      return;
    }

    updating.value = true;
    if (couponCode === loyaltyCouponCode.value?.value) {
      try {
        error.value.applyOrRemoveCoupon = undefined;
        const { data } = await $carts.removeLoyaltyCoupon(cart.value?.id, cart.value?.version);
        await setCart(data?.cart);
      }
      catch (err) {
        if (err instanceof Error) {
          error.value.applyOrRemoveCoupon = err.message;
        }
        send({
          message: ERROR_MESSAGES.PROBLEM_REMOVING_COUPON,
          type: 'danger',
        });
      }
      finally {
        updating.value = false;
      }
    }
    else {
      try {
        if (!cart.value) {
          throw new Error('Cart not found');
        }

        error.value.applyOrRemoveCoupon = undefined;
        const { data } = await $carts.removeCoupon(cart.value.id, cart.value.version, { discountId });

        await setCart(data?.cart);
      }
      catch (err) {
        if (err instanceof Error) {
          error.value.applyOrRemoveCoupon = err.message;
        }

        send({
          message: ERROR_MESSAGES.PROBLEM_REMOVING_COUPON,
          type: 'danger',
          icon: 'error',
        });
      }
      finally {
        updating.value = false;
      }
    }
  };

  const setNewsletterState = async (newsletterState: boolean) => {
    if (!cart.value) {
      return;
    }

    updating.value = true;
    try {
      const { data } = await $carts.setNewsletterState(cart.value.id, cart.value.version, { newsLetterState: newsletterState });

      await setCart({ ...cart.value, ...data?.cart });
    }
    catch (err) {
      if (err instanceof Error) {
        error.value.setNewletter = err.message;
      }
      send({
        message: 'An error occured',
        type: 'danger',
      });
    }
    finally {
      updating.value = false;
    }
  };

  const removeItem = async (lineItemId: string) => {
    updating.value = true;
    try {
      if (!cart.value?.id) {
        throw new Error('Cart not found');
      }

      const { data } = await $carts.removeItem(cart.value.id, cart.value?.version, { lineItemId });

      await setCart(data?.cart);
    }
    catch (err) {
      if (err instanceof Error) {
        error.value.updateItemsInCart = err.message;
      }
      send({
        message: 'An error occured',
        type: 'danger',
      });
    }
    finally {
      updating.value = false;
    }
  };

  const updateQty = async (lineItemId: string, quantity: number) => {
    updating.value = true;
    try {
      const { data } = await $carts.updateQty(cart.value?.id, cart.value?.version, { lineItemId, quantity });

      await setCart(data?.cart);
    }
    catch (err) {
      if (err instanceof Error) {
        error.value.updateItemsInCart = err.message;
      }
      send({
        message: 'An error occured',
        type: 'danger',
      });
    }
    finally {
      updating.value = false;
    }
  };

  const addItem = async ({ breadcrumbs = [], ...params }) => {
    updating.value = true;

    try {
      if (!cart.value) {
        error.value.createCart = undefined;
        const { data } = await $carts.createCart();
        await setCart(data?.cart, false);
      }

      await refresh();

      const { data } = await $carts.addToCart(
        cart.value?.id,
        cart.value?.version,
        {
          ...params,
          breadcrumbs: breadcrumbs.map(({ text }) => text).join(' / '),
        });

      setCart(data?.cart, false);

      send({
        message: 'addToCartMessage',
        icon: 'add_to_cart',
        type: 'success',
      });
    }
    catch (err) {
      if (err instanceof Error) {
        error.value.createCart = err.message;
        error.value.addItem = err.message;
      }

      send({
        message: 'somethingWentWrong',
        type: 'danger',
      });
    }
    finally {
      updating.value = false;
    }
  };

  const removeActiveCart = async () => {
    setCart(null, false);
  };

  const recalculateCart = async () => {
    try {
      if (!cart?.value?.lineItems.length) {
        return { status: 'no items in cart to recalculate' };
      };
      const { data } = await $carts.recalculateCart(cart.value?.key ?? '-');

      cart.value = data.cart;

      if (data.actions.length > 0) {
        data.actions.forEach((action) => {
          if (action.code === 'priceMismatchAfterRecalculate' && data.actions.length > 1) {
            return;
          }
          send({
            message: getErrorMessage(action.code),
            type: 'warning',
          });
        });

        return { status: 'recalculated' };
      }
      return { status: 'ok' };
    }
    catch (error) {
      return error;
    }
  };

  return {
    cart: computed(() => cart.value),
    loading: computed(() => loading.value),
    error: computed(() => ({ ...error?.value })),
    updating: computed(() => updating.value),

    load,
    addItem,
    refresh,
    setCart,
    updateQty,
    removeItem,
    applyCoupon,
    removeCoupon,
    setNewsletterState,
    removeActiveCart,
    recalculateCart,
  };
};
